import { Link } from "react-router";
import type { ActorDTO, CostSummary, EventDTO } from "@roomate/core";
import { getToken, useActors, useCosts, useEvents, useMe, useMetrics } from "../api.ts";
import { useCurrentProject } from "../project.tsx";
import { money } from "./Run.tsx";
import { Action, Avatar, Bento, Disclosure, Page, PageHeader, Stat, Tile } from "../calm.tsx";
import { describeEvent, formatMin, useNames } from "../ui.tsx";

/*
 * Activity: what has happened, what it costs, and how the team is doing — read like Task.tsx,
 * not like a log viewer. Stat tiles up front, the raw feed grouped by hour and folded after
 * the first couple of hours.
 */

const pct = (v: number | null) => (v === null ? "—" : `${Math.round(v * 100)}%`);

export function ActivityPage() {
  const costs = useCosts(7).data;
  const metrics = useMetrics().data;
  // Scope the feed only when there is something to scope between: with one project the feed keeps
  // its project-less events (actor, node, rule) exactly as before.
  const { projectId, scoped, ready } = useCurrentProject();
  const events = useEvents(scoped ? projectId : undefined, 60, ready);
  const actors = useActors().data;
  const me = useMe().data!;
  const name = useNames(actors);
  const list = [...(events.data ?? [])].reverse();

  return (
    <Page wide>
      <PageHeader
        title="Activity"
        meta={<span className="text-ink-3">What happened, what it cost, how the team is doing{metrics ? ` · last ${metrics.windowDays} days` : ""}</span>}
        actions={
          me.access === "admin" ? (
            <Action onClick={exportJsonl} testId="activity-export">
              Export JSONL
            </Action>
          ) : undefined
        }
      />

      <Bento className="mt-8">
        <SpendTile costs={costs} />
        {metrics && (
          <>
            <Tile span={1}>
              <Stat
                value={formatMin(metrics.humanWaitMin.median)}
                label={
                  <>
                    human wait (median)
                    <br />
                    <span className="text-ink-3">
                      p90 {formatMin(metrics.humanWaitMin.p90)} · {metrics.humanWaitMin.samples} decisions
                    </span>
                  </>
                }
              />
            </Tile>
            <Tile span={1}>
              <Stat
                value={String(metrics.decisions.open)}
                label={
                  <>
                    open decisions
                    <br />
                    <span className="text-ink-3">{metrics.decisions.overdue} over 4h</span>
                  </>
                }
                tone={metrics.decisions.overdue > 0 ? "signal" : undefined}
              />
            </Tile>
            <Tile span={1}>
              <Stat
                value={String(metrics.tasks.done)}
                label={
                  <>
                    done
                    <br />
                    <span className="text-ink-3">{metrics.tasks.reopened} reopened</span>
                  </>
                }
              />
            </Tile>
            <Tile span={1}>
              <Stat
                value={pct(metrics.tasks.autoDoneRate)}
                label={
                  <>
                    auto-done
                    <br />
                    <span className="text-ink-3">{metrics.tasks.autoDone} tasks</span>
                  </>
                }
                tone={metrics.tasks.autoDoneRate !== null && metrics.tasks.autoDoneRate >= 0.5 ? "ok" : undefined}
              />
            </Tile>
          </>
        )}

        {metrics && (
          <Tile span={2} title="Quality">
            <div className="flex items-center gap-8">
              <Stat
                value={pct(metrics.spotChecks.defectRate)}
                label={
                  <>
                    spot-check defects
                    <br />
                    <span className="text-ink-3">
                      {metrics.spotChecks.issues}/{metrics.spotChecks.answered} found issues
                    </span>
                  </>
                }
                tone={metrics.spotChecks.defectRate === null ? undefined : metrics.spotChecks.defectRate > 0 ? "danger" : "ok"}
              />
              <Stat value={pct(metrics.provenanceCoverage)} label="work with reasoning recorded" />
            </div>
          </Tile>
        )}

        <FeedTile events={list} actors={actors} name={name} />
      </Bento>
    </Page>
  );
}

function SpendTile({ costs }: { costs: CostSummary | undefined }) {
  if (!costs) return null;
  const top = costs.byActor.slice(0, 4);
  return (
    <Tile span={2}>
      <Stat value={money(costs.totalUsd)} label={`model spend · last ${costs.windowDays} days`} />
      {top.length > 0 && (
        <div className="mt-4 flex items-center -space-x-2">
          {top.map((a) => (
            <Avatar key={a.actorId} name={a.name} kind={a.kind} size={30} />
          ))}
          {costs.byActor.length > top.length && <span className="ml-3 text-[12.5px] text-ink-3">+{costs.byActor.length - top.length} more</span>}
        </div>
      )}
      {costs.byActor.length === 0 && (
        <p className="mt-3 text-[13px] text-ink-3">No sessions reported yet. Runs started by Roomate report automatically; set the telemetry env for interactive sessions.</p>
      )}
      <Disclosure summary="Breakdown" className="mt-4">
        <ul className="space-y-2">
          {costs.byActor.map((a) => (
            <li key={a.actorId} className="flex items-center gap-2.5 text-[13px]">
              <Avatar name={a.name} kind={a.kind} size={22} />
              <span className="min-w-0 flex-1 truncate">{a.name}</span>
              <span className="shrink-0 text-ink-3">
                {a.sessions} session{a.sessions === 1 ? "" : "s"}
              </span>
              <span className="shrink-0 tabular-nums">{money(a.costUsd)}</span>
            </li>
          ))}
        </ul>
        {costs.byProject.length > 0 && (
          <ul className="mt-3 space-y-1.5 border-t border-rule/60 pt-3 text-[12.5px]">
            {costs.byProject.map((p) => (
              <li key={p.projectId} className="flex justify-between gap-3">
                <span className="text-ink-2">{p.name}</span>
                <span className="tabular-nums text-ink-3">
                  {p.runs} run{p.runs === 1 ? "" : "s"} · {money(p.costUsd)}
                </span>
              </li>
            ))}
          </ul>
        )}
        <p className="mt-3 text-[11px] text-ink-3">Client-side estimates from Claude Code, not your bill.</p>
      </Disclosure>
    </Tile>
  );
}

type Group = { key: number; label: string; items: EventDTO[] };

function groupByHour(events: EventDTO[]): Group[] {
  const groups: Group[] = [];
  for (const e of events) {
    const d = new Date(e.ts);
    d.setMinutes(0, 0, 0);
    const key = d.getTime();
    const last = groups.at(-1);
    if (last && last.key === key) last.items.push(e);
    else groups.push({ key, label: hourLabel(key), items: [e] });
  }
  return groups;
}

function hourLabel(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  const hour = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
  const dayDiff = Math.round((new Date(now).setHours(0, 0, 0, 0) - new Date(d).setHours(0, 0, 0, 0)) / 86_400_000);
  if (dayDiff === 0) return hour;
  if (dayDiff === 1) return `Yesterday · ${hour}`;
  return `${d.toLocaleDateString([], { month: "short", day: "numeric" })} · ${hour}`;
}

function FeedTile({ events, actors, name }: { events: EventDTO[]; actors: ActorDTO[] | undefined; name: (id: string | null | undefined) => string }) {
  const groups = groupByHour(events);
  const visible = groups.slice(0, 2);
  const rest = groups.slice(2);

  const row = (e: EventDTO) => (
    <li key={e.seq} className="flex gap-3 py-1.5 text-[13px]" data-testid="activity-event">
      <span className="w-11 shrink-0 tabular-nums text-ink-3">{new Date(e.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}</span>
      <span className="min-w-0 flex-1 text-ink-2">
        {describeEvent(e, name)}
        {e.subjectType === "task" && (
          <>
            {" · "}
            <Link to={`/tasks/${e.subjectId}`} className="text-ink-3 hover:text-ink">
              open
            </Link>
          </>
        )}
      </span>
    </li>
  );

  const group = (g: Group, i: number) => (
    <div key={`${g.key}-${i}`}>
      <div className="mb-1.5 flex items-center gap-2">
        <GroupAvatars events={g.items} actors={actors} />
        <h3 className="text-[12.5px] font-medium text-ink-2">{g.label}</h3>
        <span className="text-[11.5px] text-ink-3">
          {g.items.length} event{g.items.length === 1 ? "" : "s"}
        </span>
      </div>
      <ol>{g.items.map(row)}</ol>
    </div>
  );

  return (
    <Tile span={4} title="Feed" testId="activity-feed">
      {groups.length === 0 ? (
        <p className="text-[13.5px] text-ink-3">No activity yet.</p>
      ) : (
        <div className="space-y-5">
          {visible.map(group)}
          {rest.length > 0 && (
            <Disclosure summary={`${rest.length} earlier hour${rest.length === 1 ? "" : "s"}`}>
              <div className="space-y-5">{rest.map(group)}</div>
            </Disclosure>
          )}
        </div>
      )}
    </Tile>
  );
}

function GroupAvatars({ events, actors }: { events: EventDTO[]; actors: ActorDTO[] | undefined }) {
  const ids = events.map((e) => e.actorId).filter((id): id is string => !!id);
  const who = Array.from(new Set(ids)).slice(0, 3);
  if (who.length === 0) return null;
  return (
    <div className="flex -space-x-1.5">
      {who.map((id) => {
        const a = actors?.find((x) => x.id === id);
        return a ? <Avatar key={id} name={a.name} kind={a.kind} size={18} /> : null;
      })}
    </div>
  );
}

async function exportJsonl() {
  const res = await fetch("/api/events/export", { headers: { authorization: `Bearer ${getToken()}` } });
  if (!res.ok) return;
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "roomate-events.jsonl";
  a.click();
  URL.revokeObjectURL(a.href);
}
